import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { User, Star, MapPin, CreditCard, Phone, Instagram, Facebook, ArrowUpRight } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { useSite } from "@/Layout";
import { trackEvent } from "@/hooks/useBusinessData";

export default function MorePage() {
  const { config } = useSite();

  const items = [
    { to: "/founder", label: "Meet Yolanda", copy: "Long Island roots. Atlanta craft.", icon: User },
    { to: "/reviews", label: "Reviews",      copy: "A decade of Yelp five-star loyalty.", icon: Star },
    { to: "/visit",   label: "Visit",        copy: "Directions, hours, contact.", icon: MapPin },
    { to: "/visit#pay", label: "Pay",        copy: "Card in studio · online soon.", icon: CreditCard },
  ];

  return (
    <div data-testid="more-page">
      <PageHeader
        eyebrow="More"
        title="Everything else."
        italicTitle="One tap away."
        testId="more-page-header"
      />

      <section className="bg-black py-16 md:py-24">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
            {items.map((it, i) => {
              const Icon = it.icon;
              return (
                <motion.div
                  key={it.to}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.55, delay: i * 0.05 }}
                >
                  <Link
                    to={it.to}
                    className="group block border border-white/15 p-6 md:p-8 hover:bg-white hover:text-black transition-colors duration-500"
                    data-testid={`more-${it.label.toLowerCase().replace(/\s/g,'-')}`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="w-10 h-10 rounded-full border border-current/25 flex items-center justify-center mb-4">
                          <Icon className="w-4 h-4" strokeWidth={1.4} />
                        </div>
                        <div className="font-serif text-2xl md:text-3xl leading-none">
                          {it.label}
                        </div>
                        <div className="text-sm mt-2 opacity-70">
                          {it.copy}
                        </div>
                      </div>
                      <ArrowUpRight className="w-5 h-5 transition-transform group-hover:-rotate-45" strokeWidth={1.3} />
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>

          <div className="mt-14 md:mt-20 border-t border-white/10 pt-10">
            <div className="eyebrow text-zinc-500 mb-5">§ Contact & Follow</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <a
                href={`tel:${config.phone_tel}`}
                onClick={() => trackEvent("call_clicked", { source: "more_page" })}
                className="flex items-center gap-4 group"
                data-testid="more-call"
              >
                <span className="w-11 h-11 rounded-full border border-white/25 flex items-center justify-center group-hover:bg-white group-hover:text-black transition-colors">
                  <Phone className="w-4 h-4" strokeWidth={1.4} />
                </span>
                <div>
                  <div className="font-serif text-xl">Call the Studio</div>
                  <div className="eyebrow text-zinc-500 mt-1">{config.phone}</div>
                </div>
              </a>
              <div className="flex items-center gap-4">
                <a
                  href={config.instagram_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  className="w-11 h-11 rounded-full border border-white/25 flex items-center justify-center hover:bg-white hover:text-black transition-colors"
                  data-testid="more-instagram"
                >
                  <Instagram className="w-4 h-4" strokeWidth={1.4} />
                </a>
                <a
                  href={config.facebook_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                  className="w-11 h-11 rounded-full border border-white/25 flex items-center justify-center hover:bg-white hover:text-black transition-colors"
                  data-testid="more-facebook"
                >
                  <Facebook className="w-4 h-4" strokeWidth={1.4} />
                </a>
                <span className="eyebrow text-zinc-500">@shecutsbarberstudio</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
